// src/utils/ReportExportUtils.js
// Utility functions for exporting capacity data and generating reports

import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';

// Note: Install xlsx package: npm install xlsx

// ============================================
// DATA COLLECTION FUNCTIONS
// ============================================

/**
 * Collect weekly capacity data for the current month
 * Returns data for all weeks that fall within the current month
 */
export const collectMonthlyCapacityData = async (clubId, pitches, pitchConfigurations, targetMonth = null) => {
  const now = new Date();
  const year = targetMonth ? parseInt(targetMonth.split('-')[0]) : now.getFullYear();
  const month = targetMonth ? parseInt(targetMonth.split('-')[1]) - 1 : now.getMonth();
  
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // Get all weeks that overlap with this month
  const weeks = [];
  let weekStart = new Date(firstDay);
  // Adjust to Monday of the first week
  const dayOfWeek = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  
  while (weekStart <= lastDay) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    weeks.push({
      start: new Date(weekStart),
      end: weekEnd,
      label: `${formatDate(weekStart)} - ${formatDate(weekEnd)}`
    });
    
    weekStart.setDate(weekStart.getDate() + 7);
  }
  
  // Collect capacity data for each week
  const weeklyData = [];
  
  for (const week of weeks) {
    const weekCapacity = await calculateWeekCapacity(clubId, pitches, pitchConfigurations, week.start);
    weeklyData.push({
      weekLabel: week.label,
      weekStart: week.start,
      weekEnd: week.end,
      pitchData: weekCapacity
    });
  }
  
  return {
    month: new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    monthKey: `${year}-${String(month + 1).padStart(2, '0')}`,
    weeks: weeklyData,
    collectedAt: new Date().toISOString()
  };
};

/**
 * Calculate capacity for a single week
 */
const calculateWeekCapacity = async (clubId, pitches, pitchConfigurations, weekStart) => {
  const pitchData = {};
  
  for (const pitch of pitches) {
    const pitchId = normalizePitchId(pitch.id);
    const pitchNum = String(pitch.id).replace(/\D/g, '');
    const config = pitchConfigurations[pitchId];
    
    pitchData[pitchId] = {
      name: pitch.name || pitchId,
      days: {}
    };
    
    // Process each day of the week
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const dateStr = toLocalYMD(date);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      
      const amCapacity = await calculateDayCapacity(clubId, pitchNum, dateStr, true, config);
      const pmCapacity = await calculateDayCapacity(clubId, pitchNum, dateStr, false, config);
      
      pitchData[pitchId].days[dayName] = {
        date: dateStr,
        am: amCapacity,
        pm: pmCapacity,
        total: Math.round((amCapacity + pmCapacity) / 2)
      };
    }
  }
  
  return pitchData;
};

/**
 * Calculate capacity for a single day (AM or PM)
 */
const calculateDayCapacity = async (clubId, pitchNum, date, isAM, pitchConfig) => {
  try {
    // New path: clubs/{clubId}/allocations/pitch-{pitchNum}/items
    const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
    const allocationsSnapshot = await getDocs(allocationsRef);
    
    // Define time slots
    const amSlots = [];
    const pmSlots = [];
    
    for (let h = 8; h < 17; h++) {
      for (let m = 0; m < 60; m += 15) {
        amSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    
    for (let h = 17; h <= 21; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === 21 && m > 15) break;
        pmSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    
    const relevantSlots = isAM ? amSlots : pmSlots;
    const sectionsCount = pitchConfig?.sectionsCount || 8;
    const sections = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].slice(0, sectionsCount);
    const totalSlots = relevantSlots.length * sectionsCount;
    
    let allocationsCount = 0;
    
    allocationsSnapshot.forEach((allocationDoc) => {
      const docId = allocationDoc.id;
      const parts = docId.split('-');
      
      if (parts.length >= 7) {
        const docDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
        if (docDate !== date) return;
        
        const timeToken = parts[3];
        const section = parts[6];
        
        if (relevantSlots.includes(timeToken) && sections.includes(section)) {
          allocationsCount++;
        }
      }
    });
    
    return totalSlots > 0 ? Math.round((allocationsCount / totalSlots) * 100) : 0;
  } catch (error) {
    console.error('Error calculating day capacity:', error);
    return 0;
  }
};

/**
 * Collect heatmap data for a month
 */
export const collectHeatmapData = async (clubId, pitches, pitchConfigurations, targetMonth = null) => {
  const now = new Date();
  const monthKey = targetMonth || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [year, month] = monthKey.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  
  const heatmapData = {};
  
  // Initialize all pitches
  for (const pitch of pitches) {
    const pitchId = normalizePitchId(pitch.id);
    const config = pitchConfigurations[pitchId];
    const sectionsCount = config?.sectionsCount || 8;
    const sections = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].slice(0, sectionsCount);
    
    heatmapData[pitchId] = {
      name: pitch.name || pitchId,
      sectionsCount,
      sections: {}
    };
    
    sections.forEach(section => {
      heatmapData[pitchId].sections[section] = {
        rawCount: 0,
        utilizationPercent: 0
      };
    });
  }
  
  // Collect allocations
  for (const pitch of pitches) {
    const pitchId = normalizePitchId(pitch.id);
    const pitchNum = String(pitch.id).replace(/\D/g, '');
    
    try {
      // New path: clubs/{clubId}/allocations/pitch-{pitchNum}/items
      const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
      const allocationsSnapshot = await getDocs(allocationsRef);
      
      allocationsSnapshot.forEach((allocationDoc) => {
        const docId = allocationDoc.id;
        const parts = docId.split('-');
        
        if (parts.length >= 7) {
          const docYear = parseInt(parts[0]);
          const docMonth = parseInt(parts[1]);
          
          if (docYear === year && docMonth === month) {
            const section = parts[6];
            
            if (heatmapData[pitchId]?.sections[section] !== undefined) {
              heatmapData[pitchId].sections[section].rawCount++;
            }
          }
        }
      });
    } catch (error) {
      console.error(`Error collecting heatmap data for pitch ${pitchId}:`, error);
    }
  }
  
  // Calculate utilization percentages
  const slotsPerDayPerSection = 52; // 16 training + 36 match
  const maxPossiblePerSection = daysInMonth * slotsPerDayPerSection;
  
  Object.keys(heatmapData).forEach(pitchId => {
    Object.keys(heatmapData[pitchId].sections).forEach(section => {
      const rawCount = heatmapData[pitchId].sections[section].rawCount;
      heatmapData[pitchId].sections[section].utilizationPercent = 
        Math.round((rawCount / maxPossiblePerSection) * 100 * 10) / 10;
    });
  });
  
  return {
    month: new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    monthKey,
    daysInMonth,
    maxPossiblePerSection,
    pitchData: heatmapData,
    collectedAt: new Date().toISOString()
  };
};

/**
 * Collect analytics data for the current month
 */
export const collectAnalyticsData = async (clubId, pitches, teamsList, targetMonth = null) => {
  const now = new Date();
  const year = targetMonth ? parseInt(targetMonth.split('-')[0]) : now.getFullYear();
  const month = targetMonth ? parseInt(targetMonth.split('-')[1]) - 1 : now.getMonth();
  
  const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  
  // Collect sessions for all teams
  const teamAnalytics = {};
  const pitchUsage = {};
  const timeSlotUsage = {};
  const dailyUsage = {};
  
  // Initialize pitch usage
  pitches.forEach(pitch => {
    const pitchId = normalizePitchId(pitch.id);
    pitchUsage[pitchId] = {
      name: pitch.name || pitchId,
      totalAllocations: 0,
      totalMinutes: 0
    };
  });
  
  // Collect data from each pitch
  for (const pitch of pitches) {
    const pitchNum = String(pitch.id).replace(/\D/g, '');
    const pitchId = normalizePitchId(pitch.id);
    
    try {
      // New path: clubs/{clubId}/allocations/pitch-{pitchNum}/items
      const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
      const allocationsSnapshot = await getDocs(allocationsRef);
      
      allocationsSnapshot.forEach((allocationDoc) => {
        const docId = allocationDoc.id;
        const data = allocationDoc.data();
        const parts = docId.split('-');
        
        if (parts.length >= 7) {
          const docDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
          
          // Check if within date range
          if (docDate >= startDate && docDate <= endDate) {
            const timeSlot = parts[3];
            const teamName = data.teamName || data.team || 'Unknown';
            const duration = data.duration || 15;
            
            // Track team analytics
            if (!teamAnalytics[teamName]) {
              teamAnalytics[teamName] = {
                allocations: 0,
                totalMinutes: 0,
                pitchesUsed: new Set(),
                daysActive: new Set()
              };
            }
            teamAnalytics[teamName].allocations++;
            teamAnalytics[teamName].totalMinutes += duration;
            teamAnalytics[teamName].pitchesUsed.add(pitchId);
            teamAnalytics[teamName].daysActive.add(docDate);
            
            // Track pitch usage
            if (pitchUsage[pitchId]) {
              pitchUsage[pitchId].totalAllocations++;
              pitchUsage[pitchId].totalMinutes += duration;
            }
            
            // Track time slot usage
            if (!timeSlotUsage[timeSlot]) {
              timeSlotUsage[timeSlot] = 0;
            }
            timeSlotUsage[timeSlot]++;
            
            // Track daily usage
            if (!dailyUsage[docDate]) {
              dailyUsage[docDate] = 0;
            }
            dailyUsage[docDate]++;
          }
        }
      });
    } catch (error) {
      console.error(`Error collecting analytics for pitch ${pitchNum}:`, error);
    }
  }
  
  // Process team analytics (convert Sets to counts)
  const processedTeamAnalytics = {};
  Object.entries(teamAnalytics).forEach(([teamName, data]) => {
    processedTeamAnalytics[teamName] = {
      allocations: data.allocations,
      totalMinutes: data.totalMinutes,
      totalHours: Math.round(data.totalMinutes / 60 * 10) / 10,
      pitchesUsed: data.pitchesUsed.size,
      daysActive: data.daysActive.size,
      avgMinutesPerDay: data.daysActive.size > 0 
        ? Math.round(data.totalMinutes / data.daysActive.size) 
        : 0
    };
  });
  
  // Find peak and low times
  const sortedTimeSlots = Object.entries(timeSlotUsage).sort(([,a], [,b]) => b - a);
  const peakTimes = sortedTimeSlots.slice(0, 5);
  const lowTimes = sortedTimeSlots.slice(-5).reverse();
  
  return {
    month: new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    monthKey: `${year}-${String(month + 1).padStart(2, '0')}`,
    dateRange: { startDate, endDate },
    teamAnalytics: processedTeamAnalytics,
    pitchUsage,
    timeSlotUsage,
    dailyUsage,
    peakTimes: peakTimes.map(([time, count]) => ({ time, count })),
    lowTimes: lowTimes.map(([time, count]) => ({ time, count })),
    summary: {
      totalTeams: Object.keys(processedTeamAnalytics).length,
      totalAllocations: Object.values(processedTeamAnalytics).reduce((sum, t) => sum + t.allocations, 0),
      totalHours: Object.values(processedTeamAnalytics).reduce((sum, t) => sum + t.totalHours, 0),
      activeDays: Object.keys(dailyUsage).length
    },
    collectedAt: new Date().toISOString()
  };
};

// ============================================
// EXCEL EXPORT FUNCTIONS
// ============================================

/**
 * Export weekly capacity data to Excel format (returns data structure for xlsx generation)
 */
export const generateCapacityExcelData = (capacityData, heatmapData, clubName) => {
  const sheets = [];
  
  // Sheet 1: Weekly Capacity Overview
  const capacityRows = [
    ['Weekly Capacity Report', '', '', '', '', '', '', '', ''],
    [`Club: ${clubName}`, '', '', '', '', '', '', '', ''],
    [`Generated: ${new Date().toLocaleDateString()}`, '', '', '', '', '', '', '', ''],
    [''],
    ['Week', 'Pitch', 'Mon AM', 'Mon PM', 'Tue AM', 'Tue PM', 'Wed AM', 'Wed PM', 'Thu AM', 'Thu PM', 'Fri AM', 'Fri PM', 'Sat AM', 'Sat PM', 'Sun AM', 'Sun PM', 'Avg']
  ];
  
  if (capacityData?.weeks) {
    capacityData.weeks.forEach(week => {
      Object.entries(week.pitchData).forEach(([pitchId, pitchInfo]) => {
        const days = pitchInfo.days;
        const row = [
          week.weekLabel,
          pitchInfo.name,
          days.Mon?.am || 0, days.Mon?.pm || 0,
          days.Tue?.am || 0, days.Tue?.pm || 0,
          days.Wed?.am || 0, days.Wed?.pm || 0,
          days.Thu?.am || 0, days.Thu?.pm || 0,
          days.Fri?.am || 0, days.Fri?.pm || 0,
          days.Sat?.am || 0, days.Sat?.pm || 0,
          days.Sun?.am || 0, days.Sun?.pm || 0
        ];
        // Calculate average
        const values = row.slice(2).filter(v => typeof v === 'number');
        const avg = values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
        row.push(avg);
        capacityRows.push(row);
      });
    });
  }
  
  sheets.push({
    name: 'Weekly Capacity',
    data: capacityRows
  });
  
  // Sheet 2: Heatmap Data
  const heatmapRows = [
    ['Monthly Usage Heatmap', '', '', '', '', '', '', '', ''],
    [`Month: ${heatmapData?.month || 'Current'}`, '', '', '', '', '', '', '', ''],
    [`Days in Month: ${heatmapData?.daysInMonth || 30}`, '', '', '', '', '', '', '', ''],
    [''],
    ['Pitch', 'Section', 'Raw Allocations', 'Utilization %']
  ];
  
  if (heatmapData?.pitchData) {
    Object.entries(heatmapData.pitchData).forEach(([pitchId, pitchInfo]) => {
      Object.entries(pitchInfo.sections).forEach(([section, data]) => {
        heatmapRows.push([
          pitchInfo.name,
          section,
          data.rawCount,
          data.utilizationPercent
        ]);
      });
    });
  }
  
  sheets.push({
    name: 'Heatmap Data',
    data: heatmapRows
  });
  
  return {
    filename: `Capacity_Report_${capacityData?.monthKey || 'export'}.xlsx`,
    sheets
  };
};

/**
 * Generate CSV string from data (simple export option)
 */
export const generateCSV = (data, headers) => {
  const csvRows = [headers.join(',')];
  
  data.forEach(row => {
    const values = row.map(value => {
      const strValue = String(value);
      // Escape quotes and wrap in quotes if contains comma
      if (strValue.includes(',') || strValue.includes('"')) {
        return `"${strValue.replace(/"/g, '""')}"`;
      }
      return strValue;
    });
    csvRows.push(values.join(','));
  });
  
  return csvRows.join('\n');
};

/**
 * Download data as CSV file
 */
export const downloadCSV = (csvString, filename) => {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============================================
// AI REPORT GENERATION
// ============================================

/**
 * Generate a comprehensive data summary for AI analysis
 */
export const generateReportDataSummary = (capacityData, heatmapData, analyticsData, clubName) => {
  const summary = {
    clubName,
    reportPeriod: capacityData?.month || analyticsData?.month || 'Current Month',
    generatedAt: new Date().toISOString(),
    
    capacitySummary: null,
    heatmapSummary: null,
    analyticsSummary: null
  };
  
  // Capacity Summary
  if (capacityData?.weeks) {
    const allCapacities = [];
    const pitchCapacities = {};
    
    capacityData.weeks.forEach(week => {
      Object.entries(week.pitchData).forEach(([pitchId, pitchInfo]) => {
        if (!pitchCapacities[pitchId]) {
          pitchCapacities[pitchId] = { name: pitchInfo.name, values: [] };
        }
        
        Object.values(pitchInfo.days).forEach(day => {
          allCapacities.push(day.am, day.pm);
          pitchCapacities[pitchId].values.push(day.am, day.pm);
        });
      });
    });
    
    // Calculate per-pitch averages
    const pitchAverages = {};
    Object.entries(pitchCapacities).forEach(([pitchId, data]) => {
      pitchAverages[pitchId] = {
        name: data.name,
        average: data.values.length > 0 
          ? Math.round(data.values.reduce((a, b) => a + b, 0) / data.values.length)
          : 0
      };
    });
    
    summary.capacitySummary = {
      weeksAnalyzed: capacityData.weeks.length,
      overallAverageUtilization: allCapacities.length > 0 
        ? Math.round(allCapacities.reduce((a, b) => a + b, 0) / allCapacities.length)
        : 0,
      pitchAverages,
      highestUtilization: Math.max(...allCapacities),
      lowestUtilization: Math.min(...allCapacities)
    };
  }
  
  // Heatmap Summary
  if (heatmapData?.pitchData) {
    const sectionData = [];
    const pitchUtilization = {};
    
    Object.entries(heatmapData.pitchData).forEach(([pitchId, pitchInfo]) => {
      const sectionUtils = Object.values(pitchInfo.sections).map(s => s.utilizationPercent);
      const avgUtil = sectionUtils.length > 0 
        ? Math.round(sectionUtils.reduce((a, b) => a + b, 0) / sectionUtils.length * 10) / 10
        : 0;
      
      pitchUtilization[pitchId] = {
        name: pitchInfo.name,
        averageUtilization: avgUtil,
        sections: pitchInfo.sections
      };
      
      Object.entries(pitchInfo.sections).forEach(([section, data]) => {
        sectionData.push({
          pitch: pitchInfo.name,
          section,
          utilization: data.utilizationPercent
        });
      });
    });
    
    // Find most and least used sections
    const sortedSections = sectionData.sort((a, b) => b.utilization - a.utilization);
    
    summary.heatmapSummary = {
      month: heatmapData.month,
      pitchUtilization,
      mostUsedSections: sortedSections.slice(0, 5),
      leastUsedSections: sortedSections.slice(-5).reverse(),
      overallAverageUtilization: sectionData.length > 0
        ? Math.round(sectionData.reduce((sum, s) => sum + s.utilization, 0) / sectionData.length * 10) / 10
        : 0
    };
  }
  
  // Analytics Summary
  if (analyticsData) {
    const teamRankings = Object.entries(analyticsData.teamAnalytics)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.totalHours - a.totalHours);
    
    summary.analyticsSummary = {
      dateRange: analyticsData.dateRange,
      totalTeams: analyticsData.summary.totalTeams,
      totalAllocations: analyticsData.summary.totalAllocations,
      totalHours: analyticsData.summary.totalHours,
      activeDays: analyticsData.summary.activeDays,
      topTeamsByUsage: teamRankings.slice(0, 10),
      peakTimes: analyticsData.peakTimes,
      lowUsageTimes: analyticsData.lowTimes,
      pitchUsage: analyticsData.pitchUsage
    };
  }
  
  return summary;
};

/**
 * Generate the AI prompt for report generation
 */
export const generateAIReportPrompt = (dataSummary) => {
  return `You are a professional sports facility analyst. Generate a comprehensive monthly facility utilization report based on the following data.

FACILITY: ${dataSummary.clubName}
REPORT PERIOD: ${dataSummary.reportPeriod}

=== CAPACITY DATA ===
${dataSummary.capacitySummary ? `
- Weeks Analyzed: ${dataSummary.capacitySummary.weeksAnalyzed}
- Overall Average Utilization: ${dataSummary.capacitySummary.overallAverageUtilization}%
- Highest Utilization Recorded: ${dataSummary.capacitySummary.highestUtilization}%
- Lowest Utilization Recorded: ${dataSummary.capacitySummary.lowestUtilization}%
- Pitch-by-Pitch Averages:
${Object.entries(dataSummary.capacitySummary.pitchAverages).map(([id, data]) => `  * ${data.name}: ${data.average}%`).join('\n')}
` : 'No capacity data available'}

=== HEATMAP ANALYSIS ===
${dataSummary.heatmapSummary ? `
- Month: ${dataSummary.heatmapSummary.month}
- Overall Section Utilization: ${dataSummary.heatmapSummary.overallAverageUtilization}%
- Most Used Sections:
${dataSummary.heatmapSummary.mostUsedSections.map(s => `  * ${s.pitch} Section ${s.section}: ${s.utilization}%`).join('\n')}
- Least Used Sections:
${dataSummary.heatmapSummary.leastUsedSections.map(s => `  * ${s.pitch} Section ${s.section}: ${s.utilization}%`).join('\n')}
` : 'No heatmap data available'}

=== TEAM ANALYTICS ===
${dataSummary.analyticsSummary ? `
- Total Teams: ${dataSummary.analyticsSummary.totalTeams}
- Total Allocations: ${dataSummary.analyticsSummary.totalAllocations}
- Total Hours Booked: ${dataSummary.analyticsSummary.totalHours}
- Active Days: ${dataSummary.analyticsSummary.activeDays}
- Peak Usage Times: ${dataSummary.analyticsSummary.peakTimes.map(t => `${t.time} (${t.count} bookings)`).join(', ')}
- Low Usage Times: ${dataSummary.analyticsSummary.lowUsageTimes.map(t => `${t.time} (${t.count} bookings)`).join(', ')}
- Top Teams by Usage:
${dataSummary.analyticsSummary.topTeamsByUsage.slice(0, 5).map((t, i) => `  ${i + 1}. ${t.name}: ${t.totalHours}hrs (${t.daysActive} days active)`).join('\n')}
` : 'No analytics data available'}

Please generate a professional report with the following sections:
1. Executive Summary (2-3 sentences highlighting key findings)
2. Capacity Utilisation Overview
3. Section Usage Analysis (from heatmap data)
4. Team Activity Insights
5. Peak & Off-Peak Analysis
6. Recommendations for Optimisation
7. Key Metrics Summary (bullet points)

IMPORTANT CONTEXT AND REQUIREMENTS:
- This is an Australian community soccer/football club, NOT a commercial facility
- The club is run by volunteers, not for profit or revenue generation
- Use Australian English spelling throughout (utilisation, optimisation, organised, centre, favour, colour, etc.)
- Recommendations should focus on:
  * Better serving club teams and members
  * Fair allocation of training time across teams
  * Reducing volunteer workload through efficient scheduling
  * Pitch maintenance and rest periods
  * Accommodating growth in junior/senior registrations
  * Coordinating with other local clubs for shared facility use
- Do NOT focus on:
  * Revenue generation or pricing strategies
  * Commercial bookings or corporate events
  * Marketing or membership drives for profit
  * Monetary targets or financial metrics

Write in a professional but accessible tone suitable for club administrators, committee members, and volunteers. Include specific numbers and percentages where relevant. Highlight any concerning patterns (underutilisation or overutilisation) and provide practical, actionable recommendations appropriate for a volunteer-run community sports club.`;
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const normalizePitchId = (pitchId) => {
  if (!pitchId) return '';
  const id = String(pitchId);
  if (id.startsWith('Pitch ')) return id;
  if (id.startsWith('pitch-')) {
    const num = id.replace('pitch-', '');
    return `Pitch ${num}`;
  }
  if (id.startsWith('pitch')) {
    const num = id.replace('pitch', '');
    return `Pitch ${num}`;
  }
  return `Pitch ${id}`;
};

const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDate = (date) => {
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

export default {
  collectMonthlyCapacityData,
  collectHeatmapData,
  collectAnalyticsData,
  generateCapacityExcelData,
  generateCSV,
  downloadCSV,
  generateReportDataSummary,
  generateAIReportPrompt
};
